import { z } from "zod";
import { MAXIMUM_MUTABLE_SUBGROUP_REVISION, MAXIMUM_SUBGROUP_DEFINITION_UTF8_BYTES, MAXIMUM_SUBGROUP_PATTERNS_PER_VALUE, MAXIMUM_SUBGROUP_VALUES, } from "./subgroup-constants.js";
export * from "./metrics.js";
export * from "./analytics.js";
export * from "./report-preferences.js";
export * from "./report-filters.js";
export * from "./reporting-constants.js";
export * from "./reporting.js";
export * from "./strategist-report.js";
export * from "./subgroup-constants.js";
export * from "./video-label.js";
export * from "./video.js";
export const APP_SCHEMA_VERSION = "202608250001_video_daily_schema_v4";
export const serviceNameSchema = z.enum(["api", "web", "jobs"]);
export const serviceStatusSchema = z.object({
    schemaVersion: z.literal(APP_SCHEMA_VERSION),
    service: serviceNameSchema,
    status: z.literal("ok"),
});
const uuid = z.string().uuid();
const workspaceEmailCandidate = z
    .string()
    .trim()
    .min(3)
    .max(320)
    .refine((value) => /^[^\s@]+@[^\s@]+$/u.test(value), "Expected one non-empty email local part and domain");
const brandSlug = z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
const bqIdentifier = z
    .string()
    .trim()
    .regex(/^[A-Za-z_][A-Za-z0-9_]{0,1023}$/);
const bqProjectId = z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/);
export const grantScopeRequestSchema = z.discriminatedUnion("scopeType", [
    z
        .object({ brandId: uuid, scopeType: z.literal("BRAND") })
        .strict(),
    z
        .object({
        brandAdAccountId: uuid,
        brandId: uuid,
        scopeType: z.literal("AD_ACCOUNT"),
    })
        .strict(),
]);
const grantScopesSchema = z
    .array(grantScopeRequestSchema)
    .min(1)
    .max(10_000);
export const applyGrantsRequestSchema = z
    .object({ scopes: grantScopesSchema })
    .strict();
export const createInvitationRequestSchema = z
    .object({
    email: workspaceEmailCandidate,
    expiresAt: z.iso.datetime({ offset: true }).optional(),
    scopes: grantScopesSchema,
})
    .strict();
export const createBrandRequestSchema = z
    .object({ name: z.string().trim().min(1).max(200), slug: brandSlug })
    .strict();
export const updateBrandRequestSchema = createBrandRequestSchema
    .partial()
    .refine((value) => value.name !== undefined || value.slug !== undefined, "At least one brand field is required");
export const saveBigQueryMappingRequestSchema = z
    .object({
    assetsTable: bqIdentifier.default("video_assets"),
    creativesTable: bqIdentifier.default("ad_creatives"),
    datasetId: bqIdentifier,
    insightsTable: bqIdentifier.default("ad_insights"),
    isActive: z.boolean().default(true),
    projectId: bqProjectId,
    unionOrder: z.number().int().min(0),
})
    .strict();
export const createUserRequestSchema = z
    .object({
    email: workspaceEmailCandidate,
    role: z.enum(["admin", "viewer"]).default("viewer"),
})
    .strict();
export const updateUserRequestSchema = z
    .object({
    isActive: z.boolean().optional(),
    role: z.enum(["admin", "viewer"]).optional(),
})
    .strict()
    .refine((value) => value.isActive !== undefined || value.role !== undefined, "At least one user field is required");
export const allowedEmailDomainRequestSchema = z
    .object({
    domain: z.string().trim().min(1).max(253),
})
    .strict();
const subgroupName = z.string().trim().min(1).max(200);
const subgroupPattern = z
    .string()
    .trim()
    .min(1)
    .max(256)
    .regex(/^[\x20-\x7e]+$/)
    .transform((value) => value.toLocaleLowerCase("en-US"));
const subgroupPatterns = z
    .array(subgroupPattern)
    .min(1)
    .max(MAXIMUM_SUBGROUP_PATTERNS_PER_VALUE)
    .superRefine((patterns, context) => {
    if (new Set(patterns).size !== patterns.length) {
        context.addIssue({
            code: "custom",
            message: "Subgroup patterns must be unique after normalization",
        });
    }
});
const newSubgroupValueSchema = z
    .object({ name: subgroupName, patterns: subgroupPatterns })
    .strict();
const existingSubgroupValueSchema = newSubgroupValueSchema
    .extend({ id: uuid })
    .strict();
function rejectDuplicateSubgroupValueNames(values, context) {
    const normalized = values.map(({ name }) => name.toLocaleLowerCase("en-US"));
    if (new Set(normalized).size !== normalized.length) {
        context.addIssue({
            code: "custom",
            message: "Subgroup value names must be unique after normalization",
        });
    }
}
function rejectOversizedSubgroupDefinition(value, context) {
    if (new TextEncoder().encode(JSON.stringify(value)).byteLength >
        MAXIMUM_SUBGROUP_DEFINITION_UTF8_BYTES) {
        context.addIssue({
            code: "custom",
            message: "Subgroup definition exceeds the encoded-size limit",
        });
    }
}
export const createSubgroupRequestSchema = z
    .object({
    name: subgroupName,
    values: z.array(newSubgroupValueSchema).min(1).max(MAXIMUM_SUBGROUP_VALUES),
})
    .strict()
    .superRefine((definition, context) => {
    const { values } = definition;
    rejectDuplicateSubgroupValueNames(values, context);
    rejectOversizedSubgroupDefinition(definition, context);
});
export const updateSubgroupRequestSchema = z
    .object({
    expectedRevision: z
        .number()
        .int()
        .min(0)
        .max(MAXIMUM_MUTABLE_SUBGROUP_REVISION),
    name: subgroupName,
    values: z
        .array(existingSubgroupValueSchema)
        .min(1)
        .max(MAXIMUM_SUBGROUP_VALUES),
})
    .strict()
    .superRefine((definition, context) => {
    const { values } = definition;
    rejectDuplicateSubgroupValueNames(values, context);
    if (new Set(values.map(({ id }) => id)).size !== values.length) {
        context.addIssue({
            code: "custom",
            message: "Subgroup value identifiers must be unique",
        });
    }
    rejectOversizedSubgroupDefinition(definition, context);
});
export const reorderSubgroupRequestSchema = z
    .object({
    expectedRevision: z
        .number()
        .int()
        .min(0)
        .max(MAXIMUM_MUTABLE_SUBGROUP_REVISION),
    valueIds: z.array(uuid).min(1).max(MAXIMUM_SUBGROUP_VALUES),
})
    .strict()
    .superRefine(({ valueIds }, context) => {
    if (new Set(valueIds).size !== valueIds.length) {
        context.addIssue({
            code: "custom",
            message: "Subgroup value identifiers must be unique",
        });
    }
});
export const subgroupRevisionRequestSchema = z
    .object({
    expectedRevision: z
        .number()
        .int()
        .min(0)
        .max(MAXIMUM_MUTABLE_SUBGROUP_REVISION),
})
    .strict();
export const createSubgroupValueRequestSchema = newSubgroupValueSchema
    .extend({
    expectedRevision: z
        .number()
        .int()
        .min(0)
        .max(MAXIMUM_MUTABLE_SUBGROUP_REVISION),
})
    .strict();
//# sourceMappingURL=index.js.map